// Small pieces shared by the attendance tabs. Colours follow the Certificates page: ink #1A1A1A,
// brand yellow #FFC200, cards with a 1px #E3E3E3 border, red #8A1C12 for problems and amber
// #6E470C / #FFF8E0 for things that need attention.
import React from "react";
import { NavLink } from "react-router-dom";
import { ConfigProvider } from "antd";
import type { SessionState, StandingRow } from "./api";

export const cx = (...names: (string | false | null | undefined)[]) => names.filter(Boolean).join(" ");

export const card = "rounded-xl border border-border bg-card";

// The app's primary colour is the brand yellow, and antd puts white text on it (Save buttons,
// ticks, picked dates), which can't be read. antd 6 takes that text colour from
// colorTextLightSolid (not Button.primaryColor), so it is set to ink for these components only.
const INK = "#1A1A1A";
const attendanceTheme = {
  components: {
    Button: { colorTextLightSolid: INK },
    Checkbox: { colorWhite: INK },
    Radio: { buttonSolidCheckedColor: INK },
    DatePicker: { colorTextLightSolid: INK },
  },
};

/** Wraps the attendance pages so nothing on them is white text on yellow. */
export function AttendanceTheme({ children }: { children: React.ReactNode }) {
  return <ConfigProvider theme={attendanceTheme}>{children}</ConfigProvider>;
}

export const STATE_LABEL: Record<SessionState, string> = {
  upcoming: "Upcoming",
  open: "Open for check-in",
  needs_closing: "Needs closing",
  closed: "Closed",
  cancelled: "Cancelled",
};

const STATE_CLASS: Record<SessionState, string> = {
  upcoming: "bg-[#EDEDEA] text-[#1A1A1A]",
  open: "bg-[#E2EEFB] text-[#1D4E89]",
  needs_closing: "bg-[#FFF4D6] text-[#6E470C]",
  closed: "bg-[#E7F4EC] text-[#14532D]",
  cancelled: "bg-[#EDEDEA] text-[#4D4D4D]",
};

export function StateChip({ state }: { state: SessionState }) {
  return (
    <span className={cx("inline-flex whitespace-nowrap rounded-full px-2.5 py-0.5 text-[13px] font-semibold", STATE_CLASS[state])}>
      {STATE_LABEL[state]}
    </span>
  );
}

export function Tile({ value, label, tone = "default" }: { value: React.ReactNode; label: string; tone?: "default" | "amber" | "red" }) {
  return (
    <div
      className={cx(
        "rounded-[10px] px-4 py-3.5",
        tone === "amber" ? "border border-[#F2D27A] bg-[#FFF8E0]" : "border border-border bg-card",
      )}
    >
      <div className={cx("text-[26px] font-semibold leading-tight", tone === "red" && "text-[#8A1C12]")}>{value}</div>
      <div className={cx("text-[13px]", tone === "amber" ? "text-[#6E470C]" : tone === "red" ? "text-[#8A1C12]" : "text-muted-foreground")}>{label}</div>
    </div>
  );
}

export function Bar({ value, max, label, tone = "ink" }: { value: number; max: number; label: string; tone?: "ink" | "red" }) {
  const width = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div role="img" aria-label={label} className="mt-1.5 h-1.5 rounded-full bg-[#E6E6E2]">
      <div className={cx("h-1.5 rounded-full", tone === "red" ? "bg-[#8A1C12]" : "bg-[#1A1A1A]")} style={{ width: `${width}%` }} />
    </div>
  );
}

/** A percentage against its minimum, with a marker where the minimum is. */
export function PercentVsMinimum({ percentage, minimum }: { percentage: number | null; minimum: number }) {
  const below = percentage !== null && percentage < minimum;
  return (
    <div className="min-w-[150px]">
      <div className="flex items-baseline gap-2">
        <span className={cx("text-[15px] font-semibold", below && "text-[#8A1C12]")}>{percentage === null ? "—" : `${percentage}%`}</span>
        <span className="text-xs text-muted-foreground">min {minimum}%</span>
      </div>
      <div
        role="img"
        aria-label={percentage === null ? `No closed sessions yet, minimum ${minimum}` : `${percentage} percent, minimum ${minimum}`}
        className="relative mt-1.5 h-1.5 rounded-full bg-[#E6E6E2]"
      >
        <div className={cx("h-1.5 rounded-full", below ? "bg-[#8A1C12]" : "bg-[#1A1A1A]")} style={{ width: `${Math.min(100, percentage ?? 0)}%` }} />
        <div className="absolute -top-1 h-3.5 w-0.5 bg-[#1A1A1A]" style={{ left: `${Math.min(100, minimum)}%` }} />
      </div>
    </div>
  );
}

const MARK_CLASS = { present: "bg-[#1A1A1A]", absent: "bg-[#C0392B]", excused: "bg-[#D6D6D6]" } as const;

export function LastSessions({ recent }: { recent: StandingRow["attendance"]["recent"] }) {
  if (!recent.length) return <span className="text-xs text-muted-foreground">No closed sessions yet</span>;
  return (
    <div role="img" aria-label={`Last ${recent.length}: ${recent.join(", ")}`} className="flex gap-[3px]">
      {recent.map((mark, index) => (
        <span key={index} className={cx("inline-block h-[18px] w-2.5 rounded-sm", MARK_CLASS[mark])} />
      ))}
    </div>
  );
}

export function LastSessionsKey() {
  return (
    <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
      <span className="inline-flex items-center gap-1.5"><span className={cx("inline-block h-3 w-2 rounded-sm", MARK_CLASS.present)} />Present</span>
      <span className="inline-flex items-center gap-1.5"><span className={cx("inline-block h-3 w-2 rounded-sm", MARK_CLASS.absent)} />Absent</span>
      <span className="inline-flex items-center gap-1.5"><span className={cx("inline-block h-3 w-2 rounded-sm", MARK_CLASS.excused)} />Excused (not counted)</span>
    </div>
  );
}

/** Tabs for the three attendance pages; each is its own URL so it can be bookmarked. */
export function AttendanceTabs({ atRisk }: { atRisk?: number }) {
  const tab = ({ isActive }: { isActive: boolean }) =>
    cx(
      "whitespace-nowrap px-3.5 py-2.5 text-sm no-underline",
      isActive ? "font-semibold text-foreground shadow-[inset_0_-2px_0_#1A1A1A]" : "text-muted-foreground hover:text-foreground",
    );
  return (
    <nav aria-label="Attendance" className="-mx-1 flex gap-1 overflow-x-auto border-b border-border px-1">
      <NavLink to="/admin/attendance/sessions" className={tab}>Sessions</NavLink>
      <NavLink to="/admin/attendance/students" className={tab}>
        Students{" "}
        {atRisk ? <span className="ml-1 rounded-full bg-[#FDECEA] px-2 py-px text-xs font-semibold text-[#8A1C12]">{atRisk} at risk</span> : null}
      </NavLink>
      <NavLink to="/admin/attendance/rules" className={tab}>Rules</NavLink>
    </nav>
  );
}

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: React.ReactNode; actions?: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 className="m-0 text-[28px] font-semibold leading-tight">{title}</h1>
        {subtitle ? <div className="mt-1 text-sm text-muted-foreground">{subtitle}</div> : null}
      </div>
      {actions ? <div className="flex flex-wrap gap-2">{actions}</div> : null}
    </div>
  );
}

/** A plain select styled like the design's filter pills. */
export function FilterSelect({ label, value, onChange, options }: { label: string; value: string; onChange: (value: string) => void; options: { value: string; label: string }[] }) {
  return (
    <label className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-[#D6D6D6] bg-card px-2.5 text-[13px]">
      {label}
      <select className="max-w-[220px] border-0 bg-transparent text-[13px] font-semibold text-foreground outline-none" value={value} onChange={(event) => onChange(event.target.value)}>
        {options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select>
    </label>
  );
}

// The old sessions form took 12-hour times without AM/PM, so "02:00" means 2 pm. No class runs
// between 1 and 6 am, so those hours are shown as afternoon.
const clockTime = (value?: string) => {
  const match = /^(\d{1,2}):(\d{2})/.exec(value || "");
  if (!match) return value || "";
  const hour = Number(match[1]);
  return `${String(hour >= 1 && hour <= 6 ? hour + 12 : hour).padStart(2, "0")}:${match[2]}`;
};

export const sessionTimes = (session: { startTime?: string; endTime?: string }) =>
  session.endTime ? `${clockTime(session.startTime)} – ${clockTime(session.endTime)}` : clockTime(session.startTime);

export const percentText = (value: number | null | undefined) => (value === null || value === undefined ? "—" : `${value}%`);
